/**
 * EV/BV inaccuracy reporting modal for coverage details.
 * @module EvBvModal
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { appendRowDataInGsheet } from "../../../api/postCall/appendRowDataInGsheet";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import {
  Demographics,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { GsheetDetails } from "../../../enums/gsheetDetails";
import { RootState } from "../../../redux/store/store";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import CustomCheckBox from "../../customCheckBox/customCheckBox";
import { ToastMessages } from "../../toasts/toastMessages";

/** Props for the {@link EvBvModal} component. */
interface EvBvModalProps {
  /** Callback to close the modal. */
  onClose?: () => void;
  /** Index of the currently active coverage tab (primary/secondary/tertiary). */
  activeTabIndex?: number;
  /** Coverage records for the order, indexed by tab position. */
  coverageData?: SingleCoverage[];
  /** Patient demographics for the order. */
  demographicsData?: Demographics;
  /** PA order ID associated with this inaccuracy report. */
  orderId?: string;
}

/**
 * Modal for reporting EV/BV data inaccuracies in coverage details.
 * Users select which financial fields (copay, co-insurance, deductibles,
 * out-of-pockets) are inaccurate, provide corrected values, and submit
 * the report to a Google Sheet. Events are logged to BigQuery.
 *
 * @param props - {@link EvBvModalProps}
 */
const EvBvModal = (props: EvBvModalProps) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [fieldComments, setFieldComments] = useState<Record<string, string>>(
    {},
  );
  const [comments, setComments] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fieldOptions = useMemo(() => {
    return [
      { id: "copay", label: "Copay" },
      { id: "co-insurance", label: "Co Insurance" },
      { id: "deductibles", label: "Deductibles" },
      { id: "out-of-pockets", label: "Out of Pockets" },
    ];
  }, []);

  const isEventTriggerRef = useRef(false);

  useEffect(() => {
    if (props?.demographicsData && props?.orderId) {
      !isEventTriggerRef.current &&
        apiWorkerManager.enqueueAndProcess({
          patient_id: props?.demographicsData?.patient_id ?? "",
          order_id: props?.orderId ?? "",
          org_id: providerDetails?.FacilityId ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name: EventsName.MODAL_SHOWN,
          additional_data: {
            screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
            modal_name: ModalNamesForBigQuery.REPORT_INACCURACY_MODAL,
            patient_name:
              props?.demographicsData?.first_name +
              " " +
              props?.demographicsData?.last_name,
            payer_name: payerName,
          },
        });
      isEventTriggerRef.current = true;
    }
  }, [props?.demographicsData, props?.orderId]);

  const handleFieldCommentChange = (fieldId: string, value: string) => {
    setFieldComments((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const deductiblesData = useMemo(() => {
    const coverage = props?.coverageData?.[props?.activeTabIndex ?? 0];
    if (!coverage) return "";
    const responsibility = coverage?.patient_responsibility;
    if (!responsibility) return "";
    const deductible = Math.round(
      Number(responsibility?.individual_deductibles ?? 0),
    );
    const deductibleMet = Math.round(
      Number(responsibility?.individual_deductibles_met ?? 0),
    );
    return `${deductible}/${deductibleMet}`;
  }, [props.coverageData, props.activeTabIndex, props.orderId]);

  const outOfPocketsData = useMemo(() => {
    const coverage = props?.coverageData?.[props?.activeTabIndex ?? 0];
    if (!coverage) return "";
    const responsibility = coverage?.patient_responsibility;
    if (!responsibility) return "";
    const outOfPocket = Math.round(
      Number(responsibility?.individual_out_of_pocket ?? 0),
    );
    const outOfPocketMet = Math.round(
      Number(responsibility?.individual_out_of_pocket_met ?? 0),
    );
    return `${outOfPocket}/${outOfPocketMet}`;
  }, [props.coverageData, props.activeTabIndex, props.orderId]);

  const handleSave = async () => {
    setIsLoading(true);
    const dataToSend = {
      order_id: props?.orderId ?? "",
      patient_mrn: props?.demographicsData?.patient_id ?? "",
      created_at: moment(
        props?.coverageData?.[props?.activeTabIndex ?? 0]?.order_created_at,
      ).format("Do MMM YYYY"),
      payer_name:
        props?.coverageData?.[props?.activeTabIndex ?? 0]?.payer_name ?? "",
      error_in_copay: selectedFields.includes("copay") ? "Yes" : "No",
      copay_from_source:
        props?.coverageData?.[props?.activeTabIndex ?? 0]
          ?.patient_responsibility?.co_pay ?? "",
      copay_correct_value: fieldComments["copay"] ?? "",
      error_in_co_insurance: selectedFields.includes("co-insurance")
        ? "Yes"
        : "No",
      co_insurance_from_source:
        props?.coverageData?.[props?.activeTabIndex ?? 0]
          ?.patient_responsibility?.co_insurance ?? "",
      co_insurance_correct_value: fieldComments["co-insurance"] ?? "",
      error_in_deductibles: selectedFields.includes("deductibles")
        ? "Yes"
        : "No",
      deductibles_from_source: deductiblesData,
      deductibles_correct_value: fieldComments["deductibles"] ?? "",
      error_in_out_of_pockets: selectedFields.includes("out-of-pockets")
        ? "Yes"
        : "No",
      out_of_pockets_from_source: outOfPocketsData,
      out_of_pockets_correct_value: fieldComments["out-of-pockets"] ?? "",
      comments: comments,
      reported_by: user?.email ?? "",
      org_id: providerDetails?.FacilityId ?? "",
    };

    const response = await appendRowDataInGsheet(
      GsheetDetails.MEDICAL_PA_DASHBOARD,
      GsheetDetails.MEDICAL_PA_EBBV_SHEET,
      Object.values(dataToSend),
    );

    if (response.hasError) {
      toast.error(ToastMessages.INACCURACY_REPORTED_FAILURE);
    } else {
      toast.success(ToastMessages.INACCURACY_REPORTED_SUCCESS);
    }
    apiWorkerManager.enqueueAndProcess({
      patient_id: props?.demographicsData?.patient_id ?? "",
      order_id: props?.orderId ?? "",
      org_id: providerDetails?.FacilityId ?? "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.INACCURACY_SAVED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
        modal_name: ModalNamesForBigQuery.REPORT_INACCURACY_MODAL,
        fields_selected: selectedFields,
        patient_name:
          props?.demographicsData?.first_name +
          " " +
          props?.demographicsData?.last_name,
        payer_name: payerName,
      },
    });
    setIsLoading(false);
    props.onClose?.();
  };

  return (
    <Modal
      dialogId="evbv-modal"
      title="Report Inaccuracy"
      onSave={handleSave}
      saveButtonText={isLoading ? "Saving..." : "Save"}
      onClose={props.onClose}
      onCancel={props.onClose}
      cancelText={"Cancel"}
      disableSave={isLoading}
    >
      <div className="sections flex w-full flex-col items-center">
        <div className="fields-section flex h-auto w-full flex-col">
          <div className="fields-section header w-full text-small text-primaryGray-2">
            Which Fields have Inaccuracy
          </div>
          <div className="flex w-full flex-col">
            {fieldOptions.map((field) => (
              <div
                key={field.id}
                className="flex h-auto w-full items-center gap-3 border-b-2 border-dotted border-primaryGray-14 last:border-b-0"
              >
                {/* Fixed width checkbox + label container */}
                <div className="flex h-16 w-2/5 items-center">
                  <CustomCheckBox
                    id={field.id}
                    label={field.label}
                    isChecked={selectedFields.includes(field.id)}
                    onCheckBoxValueChange={(data) => {
                      let selectedFieldsForEvent: string[] = [];
                      if (data.value) {
                        setSelectedFields([...selectedFields, field.id]);
                        selectedFieldsForEvent = [...selectedFields, field.id];
                      } else {
                        setSelectedFields(
                          selectedFields.filter((id) => id !== field.id),
                        );
                        selectedFieldsForEvent = selectedFields.filter(
                          (id) => id !== field.id,
                        );
                        setFieldComments((prev) => {
                          const updated = { ...prev };
                          delete updated[field.id];
                          return updated;
                        });
                      }

                      apiWorkerManager.enqueueAndProcess({
                        patient_id: props?.demographicsData?.patient_id ?? "",
                        order_id: props?.orderId ?? "",
                        org_id: providerDetails?.FacilityId ?? "",
                        user_id: user?.id ?? "",
                        user_email: user?.email ?? "",
                        event_name:
                          EventsName.CHECK_BOX_CLICKED +
                          " (" +
                          data.name +
                          " - " +
                          data.value +
                          ")",
                        additional_data: {
                          screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                          modal_name:
                            ModalNamesForBigQuery.REPORT_INACCURACY_MODAL,
                          fields_selected: selectedFieldsForEvent,
                          payer_name: payerName,
                        },
                      });
                    }}
                  />
                </div>

                {/* Text input */}
                {selectedFields.includes(field.id) && (
                  <div className="flex w-3/5 items-center gap-2">
                    <input
                      type="text"
                      className="w-full rounded border border-gray-300 p-2 text-tiny font-normal"
                      placeholder={
                        field.id === "out-of-pockets" ||
                        field.id === "deductibles"
                          ? "Enter value in format TOTAL/MET"
                          : "Enter the correct value"
                      }
                      value={fieldComments[field.id] ?? ""}
                      onChange={(e) =>
                        handleFieldCommentChange(field.id, e.target.value)
                      }
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* General Comments Section */}
        <div className="comments-section w-full">
          <div className="comments-section header mb-2 text-small font-normal text-primaryGray-2">
            Comments
          </div>
          <div className="comments-section comments-textarea">
            <textarea
              className="h-20 w-full rounded border border-gray-300 p-2 text-tiny font-normal"
              placeholder="Enter your general comments here"
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EvBvModal;
